import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RiskBadge } from "@/components/ProjectMetrics";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, PieChart, Pie, Cell, Legend,
} from "recharts";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const RISK_COLORS = { GREEN: "#059669", YELLOW: "#F59E0B", ORANGE: "#EA580C", RED: "#DC2626" };

export default function Reports() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [progress, setProgress] = useState(null);
  useEffect(() => { api.get("/dashboard/executive").then((r) => setData(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (["SUPER_ADMIN", "DIREKTUR", "MANAGER_OPERASIONAL", "PIMPINAN_PROYEK"].includes(user?.role)) {
      api.get("/dashboard/progress").then((r) => setProgress(r.data)).catch(() => {});
    }
  }, [user?.role]);

  if (!data) return <div className="space-y-4"><Skeleton className="h-10 w-48" /><Skeleton className="h-80" /></div>;
  const s = data.stats;
  const riskData = [
    { name: "On Track", value: s.on_track, key: "GREEN" },
    { name: "At Risk", value: s.at_risk, key: "YELLOW" },
    { name: "High Risk", value: s.high_risk, key: "ORANGE" },
    { name: "Delayed", value: s.delayed, key: "RED" },
  ].filter((d) => d.value > 0);
  const barData = data.projects.slice(0, 10).map((p) => ({ name: p.code, Target: p.metrics.total_target, Diproses: p.metrics.processed }));

  return (
    <div className="space-y-6">
      <div>
        <p className="uppercase text-[10px] tracking-wider font-semibold text-muted-foreground">Analitik Eksekutif</p>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Laporan</h1>
      </div>
      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="p-5 lg:col-span-2">
          <h3 className="font-heading font-semibold mb-4">Target vs Diproses per Proyek</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={barData} margin={{ left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
              <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
              <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))", fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="Target" fill="hsl(var(--chart-1))" radius={[4, 4, 0, 0]} />
              <Bar dataKey="Diproses" fill="hsl(var(--chart-2))" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card className="p-5">
          <h3 className="font-heading font-semibold mb-4">Distribusi Risiko</h3>
          {riskData.length === 0 ? <p className="text-sm text-muted-foreground py-10 text-center">Tidak ada data risiko.</p> : (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={riskData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={3}>
                  {riskData.map((d) => <Cell key={d.key} fill={RISK_COLORS[d.key]} />)}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))", fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>
      <Card className="overflow-hidden">
        <div className="p-5 border-b border-border"><h3 className="font-heading font-semibold">Ringkasan Proyek</h3></div>
        <div className="overflow-x-auto scrollbar-thin">
          <Table>
            <TableHeader><TableRow className="bg-muted/50">
              <TableHead>Proyek</TableHead><TableHead>Target</TableHead><TableHead>Diproses</TableHead>
              <TableHead>Progress</TableHead><TableHead>Risiko</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {data.projects.map((p) => (
                <TableRow key={p.id}>
                  <TableCell><p className="font-semibold">{p.name}</p><p className="text-xs text-muted-foreground">{p.code}</p></TableCell>
                  <TableCell className="tabular-nums">{p.metrics.total_target}</TableCell>
                  <TableCell className="tabular-nums">{p.metrics.processed}</TableCell>
                  <TableCell className="tabular-nums">{p.metrics.completion_pct}%</TableCell>
                  <TableCell><RiskBadge risk={p.metrics.risk} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      {progress && (
        <Card className="p-5" data-testid="progress-report">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h3 className="font-heading font-semibold">Progress Tim (Meter Linear)</h3>
            <span className="text-xs text-muted-foreground">Rasio Dinamis Berkas per mL</span>
          </div>
          <div className="grid sm:grid-cols-3 gap-4 mb-5">
            <div className="rounded-lg border border-border p-3">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Total Disetujui</p>
              <p className="text-2xl font-bold tabular-nums mt-1">{progress.overview.total_approved}</p>
            </div>
            <div className="rounded-lg border border-border p-3">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Total mL</p>
              <p className="text-2xl font-bold tabular-nums mt-1">{progress.overview.total_mL}</p>
            </div>
            <div className="rounded-lg border border-border p-3">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Kontributor</p>
              <p className="text-2xl font-bold tabular-nums mt-1">{progress.overview.users.length}</p>
            </div>
          </div>
          <div className="overflow-x-auto scrollbar-thin mb-6">
            <Table>
              <TableHeader><TableRow className="bg-muted/50">
                <TableHead>Nama</TableHead><TableHead>Role</TableHead><TableHead>Disetujui</TableHead><TableHead>mL</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {progress.overview.users.map((u) => (
                  <TableRow key={u.user_id}>
                    <TableCell className="font-medium">{u.name}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{u.role}</TableCell>
                    <TableCell className="tabular-nums">{u.approved_count}</TableCell>
                    <TableCell className="tabular-nums font-semibold">{u.mL}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {progress.by_project.length > 0 && (
            <div className="space-y-3">
              <p className="text-sm font-semibold">Per Proyek</p>
              {progress.by_project.map((p) => (
                <div key={p.project_id} className="rounded-lg border border-border p-3" data-testid={`progress-project-${p.project_id}`}>
                  <div className="flex items-center justify-between mb-2 flex-wrap gap-1">
                    <p className="font-medium text-sm">{p.project_name} <span className="text-xs text-muted-foreground">({p.project_code})</span></p>
                    <span className="text-xs font-semibold tabular-nums">{p.total_approved} arsip · {p.total_mL} mL</span>
                  </div>
                  <div className="space-y-1">
                    {p.users.map((u) => (
                      <div key={u.user_id} className="flex items-center justify-between text-xs">
                        <span>{u.name}</span>
                        <span className="tabular-nums text-muted-foreground">{u.approved_count} · {u.mL} mL</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
